// Records linked to a record, as a compact table with chosen columns (sales Пр6): the building block of app tabs on a card —
// an order's items and payments, a ticket's replacements. Opening a row opens that record.
// The same table shows records picked by a field value instead of a relation: the letters of a template (platform П9).

import { useMemo, type ReactNode } from 'react';
import { recordWords, type Id, type RecordObj, type SortKey } from '@confdeck/core-shared';
import { FieldValue } from '../fields/FieldValue.tsx';
import { navigate } from '../shell/router.ts';
import { useLinked, useMeta, useQuery, useRecord, useSync, useTypesTick } from '../sync/react.tsx';
import { SYSTEM_COLUMNS, systemValue } from '../table/system-columns.tsx';
import s from './linked.module.css';
import t from '../table/compact.module.css';

/** Records of a section picked by a field value, not by a relation. */
export interface TableSelect {
  /** The section's key: «email_message» and the like. */
  typeKey: string;
  /** The field and the value that pick the records. */
  where: { field: string; value: string };
  /** Newest first by default. */
  sort?: SortKey[];
  /** How many records to show; the count above the title stays the whole number. */
  limit?: number;
}

interface Column { key: string; label: string; render: (rec: RecordObj) => ReactNode }

/** `optional`: a field that may be absent (its feature switched off, decision 025) or hidden by the record's words — then nothing is drawn instead of a notice. */
export function LinkedTable({ recordId, field = '', columns, empty = 'Записей нет', title, select, optional }: { recordId?: Id; field?: string; columns: string[]; empty?: string; title?: string; select?: TableSelect; optional?: boolean }) {
  const meta = useMeta();
  const client = useSync();
  const rec = useRecord(recordId);
  const linked = useLinked(rec, field);
  const f = rec ? meta.field(rec.typeId, field) : undefined;
  const target = f ? meta.relationTarget(f) : undefined;
  const selType = select ? meta.typeByKey(select.typeKey) : undefined;
  const selFilter = useMemo(() => select ? { field: select.where.field, op: 'eq' as const, value: select.where.value } : null, [select?.where.field, select?.where.value]);
  const selQuery = useQuery(selType?.id ?? '', selFilter, select?.sort ?? [], '');
  useTypesTick([target?.typeId, selType?.id].filter((x): x is Id => !!x));
  if (!select && !rec) return null;
  // A missing or archived field is said aloud: an empty tab would look like «no records».
  // …or one the record's words hide (R-E9, TypeDef.wording): a product without a date has no hall screens.
  if (!select && optional && (!f || !target || recordWords(meta, rec, client.store).hidden.has(field))) return null;
  if (!select && (!f || !target)) return <div className={s.empty}>Поле «{field}» недоступно в этом разделе</div>;
  if (select && !selType) return <div className={s.empty}>Раздел «{select.typeKey}» недоступен</div>;
  const typeId = select ? selType!.id : target!.typeId;
  const ids = select ? selQuery.ids : linked;
  const shown = select?.limit ? ids.slice(0, select.limit) : ids;
  // A column is a field of the section or a system prop («Создано»); both are drawn the same way as in the main table.
  const cols = columns.map((key): Column | null => {
    const field = meta.field(typeId, key);
    if (field) return { key, label: field.name, render: (r) => <FieldValue rec={r} field={field} /> };
    if (SYSTEM_COLUMNS[key]) return { key, label: SYSTEM_COLUMNS[key]!.label, render: (r) => systemValue(key, r, meta) };
    return null;
  }).filter((c): c is Column => !!c);
  return (
    <section className={s.box}>
      {title && <h3 className={s.title}>{title}<span className={s.count}>{ids.length || ''}</span></h3>}
      {shown.length ? (
        <table className={t.table}>
          <thead><tr>{cols.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
          <tbody>
            {shown.map((rid) => { const r = client.store.record(rid); return r ? (
              <tr key={rid} onClick={() => navigate(`/r/${rid}`)} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/r/${rid}`); }}>
                {cols.map((c) => <td key={c.key}>{c.render(r)}</td>)}
              </tr>
            ) : null; })}
          </tbody>
        </table>
      ) : <div className={s.empty}>{empty}</div>}
      {select?.limit && ids.length > select.limit && <div className={s.more}>Показаны последние {select.limit} из {ids.length}</div>}
    </section>
  );
}

/** Chosen fields of a record as label — value rows, a section of an app tab (the PC team of a talk). Empty values say so. */
export function FieldList({ recordId, fields, title }: { recordId: Id; fields: string[]; title?: string }) {
  const meta = useMeta();
  const rec = useRecord(recordId);
  if (!rec) return null;
  const defs = fields.map((k) => meta.field(rec.typeId, k)).filter((x): x is NonNullable<typeof x> => !!x && !x.archivedAt);
  return (
    <section className={s.box}>
      {title && <h3 className={s.title}>{title}</h3>}
      <dl className={s.list}>
        {defs.map((f) => {
          const v = rec.data[f.key];
          const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length);
          return <div key={f.id} className={s.row}><dt>{f.name}</dt><dd>{empty ? <span className={s.empty}>не указано</span> : <FieldValue rec={rec} field={f} />}</dd></div>;
        })}
      </dl>
    </section>
  );
}
